import { ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import AppContext from '../../context/AppContext';
import { ReactComponent as FolderIcon } from '../../assets/icons/ic_action_folder.svg';
import { ReactComponent as SmartIcon } from '../../assets/icons/ic_action_folder_smart.svg';
import { ReactComponent as GarminConnectIcon } from '../../assets/icons/ic_logo_garmin_connect.svg';
import { GARMIN_FOLDER_NAME } from '../../login/garmin/garminApi';
import styles from '../trackfavmenu.module.css';
import dropOverlayStyles from '../../frame/components/dropOverlay.module.css';
import GroupActions from '../actions/GroupActions';
import ActionsMenu from '../actions/ActionsMenu';
import MenuItemWithLines from '../components/MenuItemWithLines';
import { useTranslation } from 'react-i18next';
import DividerWithMargin from '../../frame/components/dividers/DividerWithMargin';
import ThreeDotsButton from '../../frame/components/btns/ThreeDotsButton';
import { fmt } from '../../util/dateFmt';
import { SMART_TYPE } from '../share/shareConstants';
import { populateSmartFolderFiles } from '../../manager/SmartFoldersManager';
import { useGpxFileDragZone } from '../../util/hooks/useGpxFileDragZone';
import { useProcessDownload } from '../../util/hooks/menu/useProcessDownload';
import { GPX } from '../../manager/GlobalManager';

export default function CloudTrackGroup({ index, group }) {
    const ctx = useContext(AppContext);
    const { t } = useTranslation();
    const folderDragHandlers = useGpxFileDragZone(group.type !== SMART_TYPE ? group.fullName : null);
    const [processDownload, setProcessDownload] = useProcessDownload(GPX, group.type, group.fullName);

    const [openActions, setOpenActions] = useState(false);
    const anchorEl = useRef(null);

    useEffect(() => {
        if (ctx.openedPopper && ctx.openedPopper !== anchorEl) {
            setOpenActions(false);
        }
    }, [ctx.openedPopper]);

    const getFolderIcon = () => {
        if (group.type === SMART_TYPE) {
            return <SmartIcon />;
        }
        if (group.name === GARMIN_FOLDER_NAME) {
            return <GarminConnectIcon />;
        }
        return <FolderIcon />;
    };

    const handleClick = (e) => {
        if (e.target !== 'path') {
            let groupToOpen = group;

            if (group.type === SMART_TYPE) {
                groupToOpen = populateSmartFolderFiles(
                    group,
                    ctx.listFiles?.uniqueFiles,
                    ctx.smartFoldersCache,
                    ctx.setSmartFoldersCache
                );
            }

            ctx.setOpenGroups((prevState) => [...prevState, groupToOpen]);
        }
    };

    const getInfoText = () => {
        if (group.type === SMART_TYPE) {
            const filesCount = group.userFilePaths?.length ?? 0;
            return `${filesCount} ${t('shared_string_gpx_files').toLowerCase()}`;
        }
        return `${fmt.monthShortDay(group.lastModifiedDate)}, ${t('shared_string_gpx_files').toLowerCase()} ${group.realSize}`;
    };

    const isDropTarget = group.type !== SMART_TYPE;
    const isDropHover = isDropTarget && ctx.gpxFileDrag?.hoverFolder === group.fullName;

    return (
        <>
            <MenuItem
                className={`${styles.group}${isDropHover ? ` ${dropOverlayStyles.groupDropTarget}` : ''}`}
                key={'group' + group.name + index}
                id={'se-menu-cloud-' + group.name}
                onClick={handleClick}
                {...(isDropTarget ? folderDragHandlers : {})}
            >
                <ListItemIcon className={styles.icon}>{getFolderIcon()}</ListItemIcon>
                <ListItemText>
                    <MenuItemWithLines name={group.name} maxLines={2} />
                    <Typography variant="body2" className={styles.groupInfo} noWrap>
                        {getInfoText()}
                    </Typography>
                </ListItemText>
                <ThreeDotsButton
                    name={'action_menu_group'}
                    tip={'shared_string_menu'}
                    id={`se-folder-actions-button-${group.name}`}
                    setOpenActions={setOpenActions}
                    anchorEl={anchorEl}
                    processDownload={processDownload}
                />
            </MenuItem>
            <DividerWithMargin margin={'64px'} {...(isDropTarget ? folderDragHandlers : {})} />
            <ActionsMenu
                open={openActions}
                setOpen={setOpenActions}
                anchorEl={anchorEl}
                actions={
                    <GroupActions
                        group={group}
                        setOpenActions={setOpenActions}
                        setProcessDownload={setProcessDownload}
                    />
                }
            />
        </>
    );
}
